import { useState, useEffect } from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface CooldownButtonProps extends ButtonProps {
  onClick: () => Promise<void> | void;
  cooldownSeconds: number;
  children: React.ReactNode;
}

export function CooldownButton({ 
  onClick, 
  cooldownSeconds, 
  children, 
  disabled, 
  ...props 
}: CooldownButtonProps) {
  const [cooldown, setCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (cooldown > 0) {
      const interval = setInterval(() => {
        setCooldown(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [cooldown]);

  useEffect(() => {
    const savedExpiry = localStorage.getItem('resend_cooldown_expiry');
    if (savedExpiry) {
      const remaining = Math.max(0, Math.floor((parseInt(savedExpiry) - Date.now()) / 1000));
      if (remaining > 0) {
        setCooldown(remaining);
      } else {
        localStorage.removeItem('resend_cooldown_expiry');
      }
    }
  }, []);

  const handleClick = async () => {
    if (cooldown > 0 || isLoading) return;

    setIsLoading(true);
    try {
      await onClick();
      const expiryTime = Date.now() + (cooldownSeconds * 1000);
      localStorage.setItem('resend_cooldown_expiry', expiryTime.toString());
      setCooldown(cooldownSeconds);
    } catch (error) {
      // Error handled by parent component
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = disabled || cooldown > 0 || isLoading;

  return (
    <Button onClick={handleClick} disabled={isDisabled} {...props}>
      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {cooldown > 0 ? `Resend in ${cooldown}s` : children}
    </Button>
  );
}
